import { SingleValueCardType } from "constants/index";
import {
  PDFReportValueItem,
  PDFReportValueType,
  ReportValueCardProps,
} from "helpers/types";
import {
  SELECT_STOCK_DISPLAY,
  isPlaceholderDisplay,
} from "./reportFieldStates";

const CARD_TYPE_TO_PDF_TYPE: Record<SingleValueCardType, PDFReportValueType> = {
  [SingleValueCardType.CURRENCY]: PDFReportValueType.CURRENCY,
  [SingleValueCardType.PERCENTAGE]: PDFReportValueType.PERCENTAGE,
  [SingleValueCardType.NUMBER]: PDFReportValueType.NUMBER,
};

// Which rows are SHARE COUNTS, read off the label because no card type says
// so: SingleValueCardType has CURRENCY, PERCENTAGE and NUMBER and nothing
// finer, and the difference matters on paper -- the renderer prints SHARES as
// a thousands-separated whole number and NUMBER through `round(float(v), 2)`,
// so a count that lands here as NUMBER prints "3400.0".
//
// "sellable" earns its own entry rather than being folded into "share":
// "Sellable Today" is a share count whose label does not contain the word,
// and it sits DIRECTLY BELOW "Shares Sold" on the Harvest card -- so getting
// it wrong prints "3,400" and "3400.0" one line apart, in the client's copy.
// It was unreachable while that row seeded NOT IMPLEMENTED and never carried
// a number.
const SHARE_COUNT_LABEL_WORDS = ["share", "sellable"];

const cardTypeToPdfType = (
  type: SingleValueCardType,
  label: string,
): PDFReportValueType =>
  SHARE_COUNT_LABEL_WORDS.some((word) => label.toLowerCase().includes(word))
    ? PDFReportValueType.SHARES
    : CARD_TYPE_TO_PDF_TYPE[type];

export const toPdfValues = (
  items: ReportValueCardProps[],
): PDFReportValueItem[] =>
  items
    // A row with no figure is OMITTED rather than exported, and there are
    // several ways to have no figure.
    //
    // The empty sentinel is one. `value` is a number here, so it would go
    // through Number("") as 0 and print "0.00%" — and for the field that
    // uses it, the participation rate, a 0 asserts the strategy captured
    // none of an upside that may never have happened. That is the exact
    // claim the blank exists to avoid on screen, so it must not be
    // reinstated in the client-facing PDF.
    //
    // A REASON is the other, and it does not imply the first. A row whose
    // figure the backend refused keeps whatever the user last saved
    // underneath the N/A, deliberately, so that a refusal cannot destroy
    // a hand-entered number (StrategyReport's harvest row). Exporting on
    // `value` alone would then print that stale saved figure into the PDF
    // as this run's result, under the row's ordinary label — the same
    // false claim as the 0 above, and worse for being plausible. The
    // export is the copy nobody can ask a follow-up question about, so a
    // row that reads N/A on screen is absent from it.
    //
    // A CONTROL row is the third, and the simplest: it has no figure at
    // all, only the select that drives the rows around it, so there is
    // nothing for the export to carry. Its `value` is never read.
    //
    // A NULLISH value is the fourth, and it is dropped for the first reason
    // above rather than as a new rule: it reads N/A on screen, and `Number`
    // would turn it into the 0 that the empty sentinel is filtered out to
    // avoid -- printing a starting value of $0.00 into the client-facing copy
    // for a figure nobody produced. It is caught explicitly because it does
    // NOT equal "": the union admits null (helpers/types carries why), so the
    // sentinel test alone lets it through.
    //
    // Available Margin Balance is the row that makes this one load-bearing
    // rather than defensive, and it is here by the operator's own choice
    // (task 3724). It is hand-entered and reads N/A until somebody types into
    // it, so an untouched row has no figure at all -- and the alternative
    // offered, printing the characters "N/A" on the client's copy, was
    // declined in favour of the rule every other N/A row on this report
    // already follows: the line is absent rather than asserting a balance of
    // zero that nobody worked out.
    //
    // "SELECT STOCK" is the fifth, and it is the one seed that must NOT
    // reach the export as its words. The other two describe the REPORT -- "No
    // Report" and "NOT IMPLEMENTED" tell a reader holding the paper copy
    // which fields are pending a run and which are pending a calculation
    // that does not exist -- where this one is an instruction to the OPERATOR
    // sitting in front of the screen, and means nothing on a client's copy.
    // The backend refuses it outright besides: its export allowlist
    // (constants.PDF_REPORT_PLACEHOLDER_VALUES) carries the other two and not
    // this one, so letting it through 400s the WHOLE PDF rather than
    // mangling one row -- and both Harvest share rows carry it after every
    // run until a stock is picked, which is the default state, not an edge.
    .filter(
      (item) =>
        item.value !== "" &&
        item.value !== null &&
        item.value !== undefined &&
        item.value !== SELECT_STOCK_DISPLAY &&
        !item.unavailableReason &&
        !item.control,
    )
    .map((item) => ({
      // A row's `action` is display-only, so anything it says has to reach
      // the export through pdfLabel or not at all — and "OEA Max Drop (All
      // Buckets)" printed over a figure covering two of them would assert
      // something the reader cannot check.
      label: item.pdfLabel ?? item.label,
      // A seed goes to the PDF as the WORDS, not as a number. Number("No
      // Report") is NaN, and the two rows above are the precedent for why
      // that matters: a figure nobody produced must never print as one. The
      // export therefore says "No Report" / "NOT IMPLEMENTED" exactly as the
      // screen does, so a reader holding the paper copy knows which fields
      // are pending a report and which are pending a calculation that does
      // not exist yet. The backend prints a non-numeric value verbatim.
      value: isPlaceholderDisplay(item.value)
        ? String(item.value)
        : Number(item.value),
      // The shares heuristic reads the DISPLAYED label, so overriding the
      // exported one cannot change a row's type.
      type: cardTypeToPdfType(item.type, item.label),
    }));
